import {
  Box,
  Grid,
  GridItem,
  IconButton,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Tooltip,
  VisuallyHidden,
  useDisclosure,
} from '@chakra-ui/react';
import { ReactComponent as ShapeSwapIcon } from 'assets/icons/a11ymenu_shape.svg';
import { SHAPE_CREATED, SHAPE_REPLACED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useCalculateNewLineWidgetData } from 'modules/Editor/components/FlowCore/hooks/useCalculateNewLineWidgetData';
import { UpdateWidget } from 'modules/Editor/store/infographSlice.types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { setWidgetElementWrapperPosition } from 'utils/dom';
import { BASIC_SHAPE_WIDGET_ITEMS, FLEXIBLE_RATIO_SHAPES } from 'widgets/BasicShapeWidget/BasicShapeWidget.config';
import { BasicShapeWidgetData } from 'widgets/BasicShapeWidget/BasicShapeWidget.types';
import { MultiEditChangeOption, MultiEditOption } from 'widgets/Widget.types';
import { groupIdCache, useEditor, useWidget } from 'widgets/sdk';

import { useFlowPort } from '../FlowCore';
import { useGetWidgetDataRect } from '../FlowCore/components/PortStepsGrid.hooks';
import { checkIsActive } from './ShapeSwap.helpers';
import { ShapeSwapProps } from './ShapeSwap.types';

export const ShapeSwap = ({ onChange }: ShapeSwapProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'shapesToolbar' });
  const { isOpen, onClose, onToggle } = useDisclosure();
  const label = t('shapeSwap.ariaLabel');

  const isMultiWidgetSelection = onChange !== undefined;

  return (
    <Popover isOpen={isOpen} onClose={onClose} isLazy closeOnBlur closeOnEsc>
      <Tooltip hasArrow placement='bottom' label={t('shapeSwap.tooltip')} bg='black'>
        <Box>
          <PopoverTrigger>
            <IconButton
              variant={'ghost-with-active'}
              aria-label={label}
              size={'sm'}
              icon={<ShapeSwapIcon width={'1.2rem'} height={'1.2rem'} />}
              isActive={isOpen}
              onClick={onToggle}
            />
          </PopoverTrigger>
        </Box>
      </Tooltip>
      <PopoverContent p={3} color='font.500' w='fit-content'>
        <VisuallyHidden>
          <PopoverHeader>{label}</PopoverHeader>
        </VisuallyHidden>
        {isMultiWidgetSelection ? (
          <MultipleSingleShapePopoverContent onChange={onChange} />
        ) : (
          <SingleShapePopoverContent />
        )}
      </PopoverContent>
    </Popover>
  );
};

const SingleShapePopoverContent = () => {
  const { boundingBox } = useEditor();
  const {
    type: formerType,
    cornerRadius,
    widthPx,
    heightPx,
    widgetId,
    topPx,
    leftPx,
    rotateDeg,
    updateWidget,
  } = useWidget<BasicShapeWidgetData>();
  const { calculateAllLineWidgetDatas } = useCalculateNewLineWidgetData();
  const { getRectOfGroupFromWidgetIdAsRect } = useGetWidgetDataRect();
  const { updatePortPosition } = useFlowPort();

  const replaceWidget = (key: string): void => {
    const defaultData = BASIC_SHAPE_WIDGET_ITEMS[BASIC_SHAPE_WIDGET_ITEMS.findIndex((item) => item.key === key)];
    const { type: newType, iconDimensions, label, options } = defaultData;

    let updateWidgetData = { type: newType, ...options };
    let connectedLineDatas: UpdateWidget[] = [];
    if (!(FLEXIBLE_RATIO_SHAPES.includes(formerType) && FLEXIBLE_RATIO_SHAPES.includes(newType))) {
      updateWidgetData.heightPx = widthPx / (iconDimensions.widthPx / iconDimensions.heightPx);

      const widgetDim = { widthPx, heightPx: updateWidgetData.heightPx, topPx, leftPx };
      const result = calculateAllLineWidgetDatas([
        {
          widgetId,
          newWidgetPos: {
            ...widgetDim,
            rotateDeg,
          },
        },
      ]);
      connectedLineDatas = result;
    }

    updateWidget(updateWidgetData, connectedLineDatas);
    setTimeout(() => {
      boundingBox.updateRect();

      // Update port position
      const groupWidgetId = groupIdCache.getParentId(widgetId);
      if (typeof updateWidgetData.heightPx === 'number' && connectedLineDatas?.length && groupWidgetId) {
        const newGroupDim = getRectOfGroupFromWidgetIdAsRect(groupWidgetId, {
          [widgetId]: { height: updateWidgetData.heightPx! },
        });
        setWidgetElementWrapperPosition(groupWidgetId, {
          widthPx: newGroupDim.width,
          heightPx: newGroupDim.height,
          topPx: newGroupDim.y,
          leftPx: newGroupDim.x,
        });
      }
      updatePortPosition(widgetId);
    }, 0);

    Mixpanel.track(SHAPE_REPLACED.eventName, {
      preset: label,
      shape_id: formerType,
      from: SHAPE_CREATED.from.Replace,
    });
  };

  return (
    <Grid templateColumns='repeat(4, minmax(0, 1fr))' gap={1}>
      {BASIC_SHAPE_WIDGET_ITEMS.map(({ key, type, label, icon }, index) => {
        // Exclude 'square', 'rectangle', and 'leaf-right' from rendering to maintain the UI layout.
        // React.Fragment is used to return nothing for these keys, preserving the grid structure.
        if (key === 'square' || key === 'rectangle' || key === 'leaf-right')
          return <React.Fragment key={`fragment-${index}`} />;
        return (
          <GridItem key={`grid-${index}`}>
            <IconButton
              variant={'ghost-with-active'}
              aria-label={`Add ${label}`}
              size={'sm'}
              key={`icon-${index}`}
              isActive={checkIsActive({ key, formerType, type, widthPx, heightPx, cornerRadius })}
              icon={icon}
              onClick={() => replaceWidget(key)}
            />
          </GridItem>
        );
      })}
    </Grid>
  );
};

const MultipleSingleShapePopoverContent = ({ onChange }: ShapeSwapProps) => {
  const replaceWidget = (key: string): void =>
    onChange!!(MultiEditOption.BasicShapeType, key, MultiEditChangeOption.OnChange);

  return (
    <Grid templateColumns='repeat(4, minmax(0, 1fr))' gap={1}>
      {BASIC_SHAPE_WIDGET_ITEMS.map(({ key, label, icon }, index) => {
        // Exclude 'square', 'rectangle', and 'leaf-right' from rendering to maintain the UI layout.
        // React.Fragment is used to return nothing for these keys, preserving the grid structure.
        if (key === 'square' || key === 'rectangle' || key === 'leaf-right')
          return <React.Fragment key={`fragment-${index}`} />;
        return (
          <GridItem key={`grid-${index}`}>
            <IconButton
              variant={'ghost-with-active'}
              aria-label={`Add ${label}`}
              size={'sm'}
              key={`icon-${index}`}
              icon={icon}
              onClick={() => replaceWidget(key)}
            />
          </GridItem>
        );
      })}
    </Grid>
  );
};
